﻿import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Container, Stack, Button, IconButton, Box, Typography } from "@mui/material";
import { selectSelectedSchema } from "@/features/mapSchema/mapSchemaSlice";
import axios from "@/features/axiosHelper";
import {
  selectCurrentArtifactDictionaryId,
  selectCurrentOutboundElement,
  setArtifactComplex,
  updateOutboundElement,
  setIsMappingClicked,
  setOutboundSchemaTable,
  setAllOutboundSchemaTables,
  setLoopRowsList,
  selectStagedOutboundEdits,
  clearItemsFromEditBatch,
} from "@/features/mapSchema/mapSchemaSlice";
import { useLocation } from "react-router-dom";
import EditOutboundMapping from "./EditOutboundMapping";
import { createAlert } from "@/features/homepage/alertSliceList";
import PreviewArtifact from "./PreviewArtifact";
import {
  resetArtifactPreview,
  initialiseArtifactPreview,
} from "@/features/connections/artifactPreviewSlice";
import ShepHorizontalMenu from "@/components/GenericComponents/ShepHorizontalMenu";
import ShepTable from "@/components/GenericComponents/ShepTable";
import { ShepTooltip } from "@/components/GenericComponents/ShepComponents";
import EditFillIcon from "@/assets/img/icons/Edit/Fill.svg?react";
import {
  useManageConnectionFooter,
  FooterButton,
} from "@/components/Integrations/ManageConnection/ManageConnectionPopupContext";
import PreviewValidationAlert from "./PreviewValidationAlert";

const EdiOutboundMap = (props) => {
  const dispatch = useDispatch();
  const { captureScroll, restoreScroll } =
    useManageConnectionFooter();

  const [previewArtifact, setPreviewArtifact] = useState(false);
  const [showLoop, setShowLoop] = useState(false);
  const [tables, setTables] = useState([]);
  const [selectionByTable, setSelectionByTable] = useState([]);
  const [elementsByTable, setElementsByTable] = useState([]);
  const [elements, setElement] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [currentTable, setCurrentTable] = useState([]); // for the EditOutboundMapping pop up
  const location = useLocation();
  const selectedSchema = useSelector(selectSelectedSchema);
  const currentArtifactId = useSelector(selectCurrentArtifactDictionaryId);
  const staged = useSelector(selectStagedOutboundEdits);

  const { ConnectionId, ArtifactType, VariantName } = selectedSchema;
  useEffect(() => {
    dispatch(
      initialiseArtifactPreview({
        connectionId: ConnectionId,
        artifactType: ArtifactType,
        variantName: VariantName,
      })
    );
    return () => {
      dispatch(resetArtifactPreview());
    };
  }, []);
  useEffect(() => {
    prepRowDataByTable();
  }, [showLoop]);
  useEffect(() => {
    loadArtifactComplex();
  }, [currentArtifactId]);

  useEffect(() => {
    getEndpointSchema();
  }, [selectedSchema]);

  useEffect(() => {
    // We need this function to push outboundSchemaTable to redux because
    // getEndpointSchema() asynchronously updates elementsByTable

    if (currentTable != null) {
      dispatch(
        setOutboundSchemaTable({
          outboundSchemaTable: elementsByTable.find(
            (x) => x.table == currentTable
          ),
        })
      );
    }
    dispatch(
      setAllOutboundSchemaTables({
        allOutboundSchemaTables: elementsByTable,
      })
    );
  }, [elementsByTable]);

  // Reset isEditing whenever the route changes
  useEffect(() => {
    return () => {
      setIsEditing(false);
    };
  }, [location.pathname]);

  const getEndpointSchema = () => {
    return axios
      .get("/mapschema/outbound/get-endpoint-schema", {
        params: {
          artifactType: selectedSchema.ArtifactType,
          connectionId: selectedSchema.ConnectionId,
          endpointSchemaId: selectedSchema.EndpointSchemaId,
        },
      })
      .then((response) => {
        let data = response.data.map((x, i) => {
          return {
            id: i,
            ...x,
          };
        });
        const loopRows = data.filter((item) => item.isLoopRow);
        dispatch(
          setLoopRowsList({
            loopRowsList: loopRows,
          })
        );
        prepRowDataByTable(data);
        setElement(data);
        return data; // allow awaiting
      })
      .catch((error) => {
        console.error("Error fetching data: ", error);
        throw error;
      });
  };

  const getTables = (data) => {
    let tableList = [];
    let initSelectedByTable = [];

    for (const item of data) {
      let table = item.Table;
      if (!tableList.includes(table)) {
        tableList.push(table);
        initSelectedByTable.push({ table: table, selected: [] });
      }
    }

    setTables(tableList);
    setSelectionByTable(initSelectedByTable);
    return tableList;
  };

  const prepRowDataByTable = (data) => {
    let tableList;
    if (data == null) {
      data = elements;
      tableList = tables;
    } else {
      tableList = getTables(data);
    }
    let newElementsByTable = [];

    tableList.map((table) => {
      newElementsByTable.push({
        table: table,
        rows: data.filter(
          (x) => x.Table === table && (!x.isLoopRow || showLoop)
        ),
      });
    });
    setElementsByTable(newElementsByTable);
  };

  const getElementValue = (row) => {
    const seg = (row.Segment || "").split(" ")[0];
    if (row.isLoopRow) return seg;
    const pos = (row.Position ?? "").toString().padStart(2, "0");
    return seg + pos;
  };

  const columns = [
    {
      field: "Loop",
      headerName: "Loop",
      width: 200,
      sortable: false,
    },
    {
      field: "Element",
      headerName: "Element",
      width: 130,
      sortable: false,
      valueGetter: (params) => getElementValue(params.row),
    },
    {
      field: "Description",
      headerName: "Description",
      flex: 5,
      maxWidth: 300,
      sortable: false,
      renderCell: (params) => (
        <ShepTooltip title={params.value ?? ""}>
          <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {params.value}
          </Box>
        </ShepTooltip>
      ),
    },
    {
      field: "DataValue",
      headerName: "Data Value",
      flex: 5,
      maxWidth: 300,
      sortable: false,
      valueGetter: (params) => {
        const raw = params.row.DataValue;
        return raw ? raw.trim().split(/\r?\n/)[0] : "";
      },
      renderCell: (params) => (
        <ShepTooltip title={params.value ?? ""}>
          <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {params.value}
          </Box>
        </ShepTooltip>
      ),
    },
    {
      field: "HandleMethod",
      headerName: "Handle Method",
      width: 200,
      sortable: false,
    },
    {
      field: "DataType",
      headerName: "Type",
      width: 75,
      sortable: false,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "MinLength",
      headerName: "Min.",
      width: 65,
      sortable: false,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "MaxLength",
      headerName: "Max.",
      width: 65,
      sortable: false,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "UserMap",
      headerName: "User Map",
      flex: 9,
      minWidth: 200,
      maxWidth: 300,
      sortable: false,
    },
    {
      field: "RequireCode",
      headerName: "Req",
      width: 60,
      sortable: false,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "ValidateCode",
      headerName: "Validate",
      width: 100,
      sortable: false,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "editAction",
      headerName: "",
      width: 60,
      sortable: false,
      renderCell: (params) => (
        <ShepTooltip title="Edit Element">
          <IconButton
            className="workflow-row" // Carries hover-only visibility
            size="small"
            onClick={() => handleEditRow(params.row)}
            disableRipple
          >
            <EditFillIcon />
          </IconButton>
        </ShepTooltip>
      ),
    },
  ];

  const onSearch = (table, event) => {
    let searchValue = event.target.value;

    let copyElementsByTable = JSON.parse(JSON.stringify(elementsByTable));
    let copyTableId = copyElementsByTable.findIndex((x) => x.table === table);
    let copyRows;

    const searchText = searchValue.toUpperCase();
    copyRows = elements.filter((x) => {
      if (x.Table !== table) return false;
      const fieldsToSearch = [
        x.Loop, //e.g <ARTIFACT>.DOCUMENTDETAIL
        x.Description, // e.g. Transaction Set Identifier, etc
        x.HandleMethod, //e.g. EMPTYDROPSEG
        x.UserMap, // The first line of users JS Script
        getElementValue(x), //e.g ST01
      ];
      return fieldsToSearch.some(
        (field) =>
          field != null &&
          field.toString().toUpperCase().includes(searchText)
      );
    });

    copyElementsByTable[copyTableId].rows = copyRows;
    setElementsByTable(copyElementsByTable);
  };

  const onSelectedRowsChange = (tableName, selectionModel) => {
    let copySelectionByTable = JSON.parse(JSON.stringify(selectionByTable));
    let i = copySelectionByTable.findIndex((x) => x.table === tableName);

    const tableData = elementsByTable.find(
      (table) => table.table === tableName
    );

    const selectedData = tableData.rows.find(
      (row) => row.id === selectionModel[0]
    );

    copySelectionByTable[i].selected = selectedData ? [selectedData] : [];

    setSelectionByTable(copySelectionByTable);
  };

  const applyAll = async () => {
    //build array payload
    const payload = Object.values(staged).map((e) => {
      const copy = { ...e };
      if (typeof copy.UserMap === "string" && copy.UserMap.startsWith("=")) {
        copy.UserMap = copy.UserMap.slice(1);
      }
      return copy;
    });

    if (payload.length === 0) {
      dispatch(
        createAlert({
          status: "Error",
          alertType: "result",
          message: "No staged changes",
          buttonLabel: "Close",
        })
      );
      return;
    }

    // define params for schema
    const params = {
      artifactType: selectedSchema.ArtifactType,
      connectionId: selectedSchema.ConnectionId,
      endpointSchemaId: selectedSchema.EndpointSchemaId,
    };

    try {
      await axios.post("/mapschema/outbound/edit-endpoint-schemas", payload, {
        params,
      });
      await getEndpointSchema(); // refresh once
      dispatch(clearItemsFromEditBatch()); // clear staging
      dispatch(
        createAlert({
          status: "Success",
          alertType: "result",
          message: `Applied ${payload.length} changes.`,
          buttonLabel: "Close",
        })
      );
      // heap.io
      window.heap.track("Outbound Mapping - Edit Map");
    } catch (err) {
      dispatch(
        createAlert({
          status: "Error",
          alertType: "result",
          message: err.response?.data ?? err.message,
          buttonLabel: "Close",
        })
      );
    }
  };

  const loadArtifactComplex = () => {
    axios
      .get("/mapschema/outbound/get-artifact-complex", {
        params: {
          artifactType: selectedSchema.ArtifactType,
          artifactDictionaryId: currentArtifactId,
        },
      })
      .then((response) => {
        dispatch(setArtifactComplex({ artifactComplex: response.data }));
      })
      .catch((error) => {
        console.error("Error fetching artifact data: ", error);
      });
  };

  const handleBackClick = () => {
    dispatch(setIsMappingClicked({ isMappingClicked: false }));
  };
  const toggleShowLoop = () => {
    setShowLoop(!showLoop);
  };
  const handlePreviewArtifact = () => {
    setPreviewArtifact(!previewArtifact);
  };

  const enterEditForRow = (row, tableName) => {
    if (!row) return;
    captureScroll();
    dispatch(updateOutboundElement({ element: row }));
    setCurrentTable(tableName);
    dispatch(
      setOutboundSchemaTable({
        outboundSchemaTable: elementsByTable.find((x) => x.table === tableName),
      })
    );
    setIsEditing(true);
  };

  const handleEditRow = (row) => {
    enterEditForRow(row, row?.Table);
  };

  // --- Error-segment navigation ---
  const navigateToErrorSegment = (elementPosition) => {
    if (!elementPosition || !elementsByTable || elementsByTable.length === 0) return false;
    const target = elementPosition.toUpperCase().trim();

    for (const table of elementsByTable) {
      if (!table?.rows?.length) continue;
      const row = table.rows.find((r) => {
        const rowSeg = (r.Segment || "").split(" ")[0].toUpperCase();
        const rowId = r.isLoopRow
          ? rowSeg
          : rowSeg + (r.Position ?? 0).toString().padStart(2, "0");
        return rowId === target;
      });
      if (row) {
        enterEditForRow(row, table.table);
        return true;
      }
    }
    return false;
  };

  return (
    <Container sx={{ padding: "0px", display: "flex", flexDirection: "column", flex: "1 1 auto", minHeight: 0, height: "100%", overflowX: "hidden" }}>
      <PreviewValidationAlert
        onNavigateToSegment={navigateToErrorSegment}
      />
      {/* Footer button rendered via portal to FooterBarHost */}
      <FooterButton
        variant="secondary"
        onClick={handleBackClick}
        hidden={isEditing || previewArtifact}
      >
        Back
      </FooterButton>

      {/* Edit panel */}
      {isEditing && (
        <Box sx={{ flex: "1 1 auto", minHeight: 0, display: "flex", flexDirection: "column" }}>
          <EditOutboundMapping
            onBack={() => {
              dispatch(updateOutboundElement({ element: null }));
              setIsEditing(false);
              setSelectionByTable((prev) =>
                prev.map((item) => ({ ...item, selected: [] }))
              );
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  restoreScroll("auto");
                });
              });
            }}
            applyAll={applyAll}
            isEditing={isEditing}
          />
        </Box>
      )}
      {/* Grids remain mounted; hidden when editing to preserve grid scroll / avoid unecessary reloads to keep editing snappy  */}
      <div style={{ display: isEditing ? "none" : "block" }}>
        <div
          style={{
            width: "100%",
            padding: "5px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {selectedSchema?.DocumentDescription && (
            <Typography
              variant="outline"
              sx={{ fontSize: 20 }}
            >
              {selectedSchema.DocumentDescription}
            </Typography>
          )}
          <ShepHorizontalMenu
            actions={[
              {
                label: previewArtifact
                  ? "Outbound Map"
                  : "Preview Document Output",
                onClick: handlePreviewArtifact,
              },
              {
                label: showLoop ? "Hide Loop Entries" : "Show Loop Entries",
                onClick: toggleShowLoop,
              },
            ]}
          />
        </div>
        {previewArtifact && (
          <PreviewArtifact
            artifactType={ArtifactType}
            connectionId={ConnectionId}
          />
        )}
        {!previewArtifact &&
          tables.map((table, i) => (
            <div key={i}>
              <ShepTable
                tableTitle={table}
                columnTitles={columns}
                dataRows={elementsByTable.find((x) => x.table === table).rows}
                handleSearchChange={(e) => onSearch(table, e)}
                handleSelectedRowChange={(selectionModel) =>
                  onSelectedRowsChange(table, selectionModel)
                }
                getRowId={(row) => row.id}
              />
            </div>
          ))}
      </div>
    </Container>
  );
};
export default EdiOutboundMap;
